import Link from 'next/link';
import { Story } from '@/lib/types';
import Label from './Label';

interface StoryCardProps {
  story: Story;
  size: 'hero' | 'large' | 'medium' | 'compact' | 'text';
  rank?: number;
}

export default function StoryCard({ story, size, rank }: StoryCardProps) {
  const base = 'group cursor-pointer transition-opacity duration-150 hover:opacity-80';
  const isClassName = story.imageBg?.startsWith('bg-');
  const imageStyle = !isClassName ? { backgroundImage: `url(${story.imageBg})` } : {};
  const imageClass = `${isClassName ? story.imageBg : 'bg-gray-100'} bg-cover bg-center bg-no-repeat`;

  if (size === 'hero') {
    return (
      <Link href={`/article/${story.slug}`} className={`block ${base}`}>
        <div className={`${imageClass} aspect-video w-full mb-4`} style={imageStyle} />
        {story.label && (
          <div className="mb-2">
            <Label type={story.label} animate={story.isLive} />
          </div>
        )}
        <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[11px] tracking-[0.12em] uppercase text-[#C8102E]">
          {story.section}
        </span>
        <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[38px] leading-[1.15] text-[#0D0D0D] mt-1 mb-3">
          {story.title}
        </h1>
        <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.55] text-[#2D2D2D] mb-3">
          {story.deck}
        </p>
        <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">
          By <span className="font-semibold text-[#0D0D0D]">{story.author}</span>
          <span className="mx-2">·</span>
          {story.publishedAt}
          <span className="mx-2">·</span>
          {story.readingTime} min read
        </div>
      </Link>
    );
  }

  if (size === 'large') {
    return (
      <Link href={`/article/${story.slug}`} className={`block ${base}`}>
        <div className={`${imageClass} aspect-video w-full mb-3`} style={imageStyle} />
        {story.label && (
          <div className="mb-2">
            <Label type={story.label} />
          </div>
        )}
        <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] leading-[1.25] text-[#0D0D0D] mb-2">
          {story.title}
        </h2>
        <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] leading-[1.55] text-[#2D2D2D] mb-2">
          {story.deck}
        </p>
        <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11.5px] text-[#6B6B6B]">
          {story.author} · {story.publishedAt}
        </div>
      </Link>
    );
  }

  if (size === 'medium') {
    return (
      <Link href={`/article/${story.slug}`} className={`block ${base}`}>
        <div className={`${imageClass} aspect-video w-full mb-3`} style={imageStyle} />
        {story.label && (
          <div className="mb-2">
            <Label type={story.label} />
          </div>
        )}
        <h3 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[17px] leading-[1.3] text-[#0D0D0D] mb-2">
          {story.title}
        </h3>
        <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B]">
          {story.author} · {story.publishedAt}
        </div>
      </Link>
    );
  }

  if (size === 'compact') {
    return (
      <Link href={`/article/${story.slug}`} className={`flex gap-3 items-start ${base}`}>
        <div className="flex-1">
          {story.label && (
            <div className="mb-1">
              <Label type={story.label} />
            </div>
          )}
          <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[14px] leading-[1.35] text-[#0D0D0D] mb-1">
            {story.title}
          </h4>
          <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B]">
            {story.publishedAt}
          </div>
        </div>
        <div className={`${imageClass} w-20 h-14 shrink-0`} style={imageStyle} />
      </Link>
    );
  }

  // text
  return (
    <Link href={`/article/${story.slug}`} className={`flex items-start gap-4 py-3 border-b border-[#D4D4D4] ${base}`}>
      {rank !== undefined && (
        <span className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] text-[#D4D4D4] leading-none shrink-0 w-8 text-right">
          {rank}
        </span>
      )}
      <div className="flex-1">
        <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[14px] leading-[1.35] text-[#0D0D0D] mb-1 group-hover:text-[#C8102E] transition-colors">
          {story.title}
        </h4>
        <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B]">
          {story.section} · {story.publishedAt}
        </div>
      </div>
    </Link>
  );
}
